/** 
- @file ClientUsers.js
- @description This file contains the ClientUsers component, which is a reusable form for displaying and editing an individual's demographic information.
*/

import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import '../page-list/PageList.css';
import styles from '../user-account/UserAccount.module.scss';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import { getClientUsers, deleteClientUser } from '../../services/clientService';
import AlertBox from '../ui/no_data_indicators/AlertBox';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ClientUsersTab = ({ individualId }) => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUnauthorized, setUnauthorized] = useState(false);
    const [userAccounts, setUserAccounts] = useState([]);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'lastName',
        direction: SortDirections.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(userAccounts);
    const selectAllRef = useRef(null);
    const { hasAttribute } = useAuthContext();
    const canEditForm = hasAttribute(SecurityAttributes.ClientAssignFullAccess);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                rowsClicked.length > 0 &&
                rowsClicked.length < filteredData.length;
        }
    }, [rowsClicked, filteredData]);

    useEffect(() => {
        if (individualId) {
            loadUserAccounts();
        }
    }, [individualId]);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        clearAllFilters();
    };

    // Compute available options considering active filters for current modal
    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column]; // Remove the current column's filter

        // Use these modified filters to filter data
        const filteredData = userAccounts.filter(userAccount =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(userAccount[key]),
            ),
        );

        // Now get all unique values for the current column from this filtered dataset
        return Array.from(new Set(filteredData.map(item => item[column])))
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    useEffect(() => {
        loadUserAccounts();
    }, []);

    const loadUserAccounts = async () => {
        try {
            const response = await getClientUsers(individualId);
            const processedResponse = response.map(item => {
                return {
                    ...item,
                    uniqueId: item.userId,
                    status: getStatusString(
                        item.tenantUserStatusId,
                    ),
                };
            });

            setUserAccounts(processedResponse);
        } catch (error) {
            console.error('Failed to fetch client users', error);
            Toast.error(getText('msg_0b66da458295'));

            if (error?.status === 400) {
                setUnauthorized(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusString = (status) => {
        switch (status) {
            case 1:
                return 'Pending';
            case 2:
                return 'Not Started';
            case 3:
                return 'Active';
            case 4:
                return 'Inactive';
            case 5:
                return 'Disabled';
            case 6:
                return 'Locked';
            default:
                return 'Undefined';
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 1:
                return 'ing-badge-pending';
            case 2:
                return 'ing-badge-inv-sent';
            case 3:
                return 'ing-badge-active';
            case 4:
                return 'ing-badge-inactive';
            case 5:
                return 'ing-badge-disabled';
            case 6:
                return 'ing-badge-error';
            default:
                return 'ing-badge-inactive';
        }
    };

    const sortedUserAccounts = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue;
            let bValue;

            if (sortConfig.key === 'tenantUserStatusId') {
                aValue = getStatusString(a[sortConfig.key]);
                bValue = getStatusString(b[sortConfig.key]);
            } else if (sortConfig.key === 'agency') {
                aValue = String(a[sortConfig.key]);
                bValue = String(b[sortConfig.key]);
            } else if (sortConfig.key === 'lastActiveDate') {
                aValue = String(a[sortConfig.key]);
                bValue = String(b[sortConfig.key]);
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }

            // Handle null or undefined values
            if (!aValue) aValue = '';
            if (!bValue) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }

            return a.userId - b.userId;
        });
    }, [filteredData, sortConfig]);

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                    prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSortLastName = () => handleSort('lastName');
    const handleSortFirstName = () => handleSort('firstName');
    const handleSortEmailAddress = () => handleSort('emailAddress');
    const handleSortAgency = () => handleSort('agency');
    const handleSortStatus = () => handleSort('status');
    const handleSortLastActive = () => handleSort('lastActiveDate');

    const handlerRowClicked = userId => {
        setRowsClicked(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId],
        );
    };

    const handleSelectAll = event => {
        if (event.target.checked) {
            setRowsClicked(filteredData.map(userAccount => userAccount.userId));
        } else {
            setRowsClicked([]);
        }
    };

    const deleteUserAccounts = () => {
        rowsClicked.forEach(userId => {
            const userAccount = userAccounts.find(
                user => user.userId === userId,
            );
            if (userAccount) {
                deleteClientUser(individualId, userAccount.id)
                    .then(response => {
                        Toast.success(
                            "User with email '" +
                            userAccount.emailAddress +
                            "' was unassigned successfully.",
                        );
                        deselectRows(rowsClicked);
                        loadUserAccounts();
                    })
                    .catch(error => {
                        console.error('Failed to delete user', error);
                        Toast.error(getText('msg_121656f1031d'));
                    });
            }
        });
    };

    const deselectRows = rowsClicked => {
        rowsClicked.forEach(userId => {
            const checkbox = document.getElementById(`user-${userId}`);
            if (checkbox) {
                checkbox.checked = false;
            }
        });
        setRowsClicked([]);
    };

    const rowAction = action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete':
                    if (rowsClicked.includes(0))
                        Toast.error(
                            getText('msg_dc17d059f59b'),
                        );
                    else {
                        showModal({
                            type: ModalTypes.DELETE_CONFIRMATION,
                            title: getText('msg_697cc22f7856'),
                            text: getText('msg_196df0deadcf'),
                            onYes: () => deleteUserAccounts(),
                            onNo: () => { },
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_f6fdbe48dc54'),
                        });
                        logEvent(LogEventType.DeleteClicked, `Client users delete clicked`);
                    }
                    break;
                default:
                    break;
            }
        } else {
            Toast.error(
                getText('msg_9bcf67dc54d5'),
            );
        }
    };

    return (
        <div>
            {isLoading ? (
                <Spinner width={50} className="client-summary-spinner" />
            ) : isUnauthorized ? (
                <AlertBox
                    title={getText('msg_53cc4f43d8a7')}
                    description={getText('msg_7de2a9f3cc6c')}
                />
            ) : (
                <>
                    {/* Screen Header with Title and Actions */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        {/* Screen Title */}
                        <p className="page-title float-start card-title ms-4"></p>
                        {/* Screen Actions */}
                        <div className="float-end d-flex align-items-center">
                            {canEditForm && (
                                <span
                                    type="button"
                                    onClick={() => rowAction('delete')}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>)}
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            {canEditForm && (
                                <span
                                    onClick={() => navigate('add-user')}
                                    type="button"
                                    className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>)}
                        </div>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        {/* Template List Table */}
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    {canEditForm && (
                                        <th className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                ref={selectAllRef}
                                                checked={
                                                    rowsClicked.length ===
                                                    filteredData.length &&
                                                    filteredData.length > 0
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        </th>)}
                                    <th>
                                        <span>{getText('msg_863cb39fbe7d')}</span>
                                        <span
                                            onClick={handleSortLastName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'lastName' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('lastName');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['lastName']?.length >
                                                0,
                                            )}
                                        </span>
                                        {activeModal === 'lastName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_b6ea992aab46')}</span>
                                        <span
                                            onClick={handleSortFirstName}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'firstName' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('firstName');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['firstName']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'firstName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_09ba557fd106')}</span>
                                        <span
                                            onClick={handleSortEmailAddress}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'emailAddress' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('emailAddress');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['emailAddress']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'emailAddress' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_5c47e26c6ad2')}</span>
                                        <span
                                            onClick={handleSortAgency}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'agency' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('agency');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['agency']?.length >
                                                0,
                                            )}
                                        </span>
                                        {activeModal === 'agency' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_bae7d5be7082')}</span>
                                        <span
                                            onClick={handleSortStatus}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'status' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('status');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['status']?.length >
                                                0,
                                            )}
                                        </span>
                                        {activeModal === 'status' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                getString={getStatusString}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_0eb2b938ad2e')}</span>
                                        <span
                                            onClick={handleSortLastActive}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'lastActiveDate' &&
                                                            sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUserAccounts.map(
                                    (userAccount, index) => (
                                        <tr
                                            key={`user-${userAccount.userId}`}

                                            className={`${rowsClicked.includes(userAccount.userId) ? 'clicked-class' : ''}`}
                                        >
                                            {userAccount.id != 0 && canEditForm && <td className="text-center">
                                                <input
                                                    id={`user-${userAccount.userId}`}
                                                    checked={rowsClicked.includes(
                                                        userAccount.userId,
                                                    )}
                                                    onChange={() =>
                                                        handlerRowClicked(
                                                            userAccount.userId,
                                                        )
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                />
                                            </td>
                                            }
                                            {userAccount.id == 0 &&
                                                <td />
                                            }
                                            <td className="titleCell">
                                                {userAccount.lastName}
                                            </td>
                                            <td className="subtitleCell">
                                                {userAccount.firstName}
                                            </td>
                                            <td className="subtitleCell">
                                                {userAccount.emailAddress}
                                            </td>
                                            <td className="subtitleCell">
                                                {userAccount.agency || '-'}
                                            </td>
                                            <td className="item-page-changed-by">
                                                <button
                                                    className={getStatusClass(
                                                        userAccount.tenantUserStatusId,
                                                    )}
                                                >
                                                    {getStatusString(
                                                        userAccount.tenantUserStatusId,
                                                    )}
                                                </button>
                                            </td>
                                            <td className="subtitleCell">
                                                {getDateFromZulu(
                                                    userAccount.lastActiveDate,
                                                ) || '-'}
                                            </td>
                                        </tr>
                                    ),
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {sortedUserAccounts.length <= 0 ? 'No users found' : ''}
                    </p>
                </>
            )}
        </div>
    );
};

export default ClientUsersTab;
